<template>
  <div class="compare-bar-wrap">
    <div class="compare-bar">
      <span class="compare-bar__label">Compare with</span>

      <div v-for="(entry, index) in values" :key="slotKey(entry, index)" class="compare-bar__slot">
        <div class="compare-bar__slot-row">
          <span class="compare-bar__letter">{{ letterFor(index) }}</span>
          <select class="form-control form-control-sm compare-bar__vessel" :value="vesselValue(entry)" @change="onVessel(index, $event)">
            <option value="">Same vessel</option>
            <option v-for="a in assets" :key="a.massId" :value="String(a.massId)">{{ a.massName }}</option>
          </select>
          <select class="form-control form-control-sm compare-bar__period" :value="entry.period.kind" @change="onKind(index, $event)">
            <option v-for="k in periodKinds" :key="k.value" :value="k.value">{{ k.label }}</option>
          </select>
          <button class="btn btn-outline-danger btn-sm compare-bar__remove" @click="remove(index)">Remove</button>
        </div>
        <div v-if="isCustomEntry(entry)" class="compare-bar__picker">
          <date-time-picker :value="customRangeFor(entry)" is-range :masks="masks" @input="onCustomRange(index, $event)" />
        </div>
        <div v-if="noticeFor(entry, index)" class="compare-bar__slot-warning">{{ noticeFor(entry, index) }}</div>
      </div>

      <button v-if="canAdd" class="btn btn-outline-secondary btn-sm compare-bar__add" @click="add">+ Add comparison</button>

      <button class="btn btn-outline-secondary btn-sm compare-bar__guide" @click="$emit('guide')">Guide</button>
    </div>
    <div v-if="warning" class="compare-bar__warning">{{ warning }}</div>
  </div>
</template>

<script>
import DateTimePicker from '@/components/directives/DateTimePicker.vue';
import { RIGHT_PERIOD_KINDS, MAX_COMPARISONS } from '@/util/summaryCompareQuery';
import { COLUMN_LETTERS } from '@/util/summaryCompareModel';

const WEEK_MS = 7 * 86400000;
const RELATIVE_KINDS = ['previous', 'lastWeek', 'lastMonth'];

export default {
  name: 'CompareBar',
  components: { DateTimePicker },
  props: {
    assets: { type: Array, default: () => [] },
    values: { type: Array, default: () => [] },
    max: { type: Number, default: MAX_COMPARISONS },
    leftMassId: { type: [String, Number], default: null },
    warning: { type: String, default: '' },
  },
  data() {
    return {
      periodKinds: RIGHT_PERIOD_KINDS,
      masks: 'DD-MM-YYYY HH:mm',
      // pesan per slot diikat ke isi entri, biar gak pindah ke slot lain pas halaman buang kolom
      notices: {},
    };
  },
  created() {
    // sengaja gak reaktif: cuma cache objek buat picker
    this.rangeCache = {};
  },
  computed: {
    // kombinasi pertama yang belum dipakai (kapal sama + periode relatif, lalu kapal lain); null kalau habis
    freeSlot() {
      const base = String(this.leftMassId);
      const signature = (massId, kind) => `${massId === null || String(massId) === base ? 'base' : massId}|${kind}`;
      const used = new Set(this.values.map((v) => signature(v.massId, v.period.kind)));
      const others = this.assets.map((a) => Number(a.massId)).filter((id) => String(id) !== base);
      const candidates = [
        ...RELATIVE_KINDS.map((kind) => ({ massId: null, kind })),
        ...others.map((massId) => ({ massId, kind: 'same' })),
        ...others.flatMap((massId) => RELATIVE_KINDS.map((kind) => ({ massId, kind }))),
      ];
      const pick = candidates.find((c) => !used.has(signature(c.massId, c.kind)));
      return pick ? { massId: pick.massId, period: { kind: pick.kind } } : null;
    },
    // kapal cuma satu: Add ilang begitu 3 periode relatif kepakai (Custom masih bisa dari dropdown)
    canAdd() {
      return this.values.length < this.max && this.freeSlot !== null;
    },
  },
  methods: {
    letterFor(index) {
      return COLUMN_LETTERS[index + 1];
    },
    // Remount kalau kapal/jenis periode ganti, biar komponen gak kepake ulang sama slot lain.
    slotKey(entry, index) {
      return `${index}|${entry.massId}|${entry.period.kind}`;
    },
    noticeKey(entry, index) {
      return `${index}|${JSON.stringify(entry)}`;
    },
    noticeFor(entry, index) {
      return this.notices[this.noticeKey(entry, index)] || '';
    },
    setNotice(entry, index, text) {
      this.notices = { ...this.notices, [this.noticeKey(entry, index)]: text };
    },
    vesselValue(entry) {
      return entry.massId !== null ? String(entry.massId) : '';
    },
    isCustomEntry(entry) {
      return entry.period.kind === 'custom';
    },
    // Objek rentang di-cache: objek baru tiap render bikin picker sync ulang & mantulin input.
    customRangeFor(entry) {
      const { startMs, endMs } = entry.period;
      const key = `${startMs}|${endMs}`;
      if (!this.rangeCache[key]) {
        this.rangeCache = { [key]: { start: new Date(startMs), end: new Date(endMs) } };
      }
      return this.rangeCache[key];
    },
    add() {
      if (!this.freeSlot) return;
      this.$emit('change', [...this.values, this.freeSlot]);
    },
    remove(index) {
      const next = this.values.slice();
      next.splice(index, 1);
      this.$emit('change', next);
    },
    onVessel(index, event) {
      const massId = event.target.value === '' ? null : Number(event.target.value);
      this.applyChange(index, massId, this.values[index].period);
    },
    onKind(index, event) {
      const kind = event.target.value;
      const entry = this.values[index];
      this.applyChange(index, entry.massId, kind === 'custom' ? this.defaultCustomPeriod(entry.period) : { kind });
    },
    onCustomRange(index, range) {
      const entry = this.values[index];
      const startMs = range.start.getTime();
      const endMs = range.end.getTime();
      // pantulan picker dengan nilai yang sama: abaikan
      if (startMs === entry.period.startMs && endMs === entry.period.endMs) return;
      if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || startMs >= endMs) {
        this.setNotice(entry, index, 'Pick an end date after the start date.');
        return;
      }
      this.applyChange(index, entry.massId, { kind: 'custom', startMs, endMs });
    },
    // Same vessel + Same as range == Base
    isSameAsLeft(massId) {
      const effective = massId === null ? this.leftMassId : massId;
      if (effective === null || effective === undefined) return false;
      return String(effective) === String(this.leftMassId);
    },
    applyChange(index, massId, period) {
      if (period.kind === 'same' && this.isSameAsLeft(massId)) {
        const switched = { massId, period: { kind: 'previous' } };
        this.setNotice(switched, index, 'Same vessel and same range — switched to previous period.');
        this.emitAt(index, switched);
        return;
      }
      this.emitAt(index, { massId, period });
    },
    emitAt(index, entry) {
      const next = this.values.slice();
      next[index] = entry;
      this.$emit('change', next);
    },
    // Custom pertama kali: pakai rentang lama kalau ada, kalau gak 7 hari terakhir.
    defaultCustomPeriod(prevPeriod) {
      if (Number.isFinite(prevPeriod.startMs) && Number.isFinite(prevPeriod.endMs)) {
        return { kind: 'custom', startMs: prevPeriod.startMs, endMs: prevPeriod.endMs };
      }
      const endMs = Date.now();
      return { kind: 'custom', startMs: endMs - WEEK_MS, endMs };
    },
  },
};
</script>

<style scoped>
/* label + Guide di baris atas, tiap slot kapal satu baris dipisah garis */
.compare-bar {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  column-gap: 8px;
  align-items: center;
}
.compare-bar__label {
  grid-row: 1;
  grid-column: 1;
  padding: 4px 0;
  font-size: 0.8125rem;
  font-weight: 600;
  color: #495057;
}
.compare-bar__guide {
  grid-row: 1;
  grid-column: 2;
  justify-self: end;
}
.compare-bar__slot {
  grid-column: 1 / -1;
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 6px 0;
  border-top: 1px solid #dde3ea;
}
.compare-bar__slot-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}
.compare-bar__letter {
  font-size: 0.8125rem;
  font-weight: 700;
  color: #495057;
  min-width: 1em;
}
.compare-bar__vessel {
  width: auto;
  min-width: 160px;
}
.compare-bar__period {
  width: auto;
  min-width: 150px;
}
.compare-bar__picker {
  max-width: 360px;
}
.compare-bar__add {
  grid-column: 1 / -1;
  justify-self: start;
  margin: 2px 0 4px;
}
.compare-bar__slot-warning,
.compare-bar__warning {
  font-size: 0.75rem;
  color: #6c757d;
}
.compare-bar__warning {
  margin-top: 4px;
}

@media (max-width: 575px) {
  .compare-bar__slot-row {
    display: grid;
    grid-template-columns: auto minmax(0, 1fr) minmax(0, 1fr) auto;
    gap: 6px;
  }
  .compare-bar__vessel,
  .compare-bar__period {
    width: 100%;
    min-width: 0;
  }
}
</style>
